// Object-driven sidebar panels for the HRMS app — pure JSON for the engine in ./engine.
// Panel `id` must match the `panelId` on its rail item in ../apps.ts and in
// apps/hrms/src/rail.ts. Layout and copy follow `PANELS` in prototypes/hrms/index.html.

import { SIDEBAR_SCHEMA_VERSION, type PanelConfig, type SidebarConfig } from '@clarix/shared-types';
import { HRMS_SHELL_FLAG as FLAG, HRMS_SHELL_PERM as P } from '../hrms-permissions';

const peoplePanel: PanelConfig = {
  id: 'people',
  title: 'People',
  sections: [
    {
      id: 'people',
      rows: [
        // Exact, or it would stay lit on every page under /hrms/people.
        { id: 'directory', label: 'Directory', icon: 'Users', to: '/hrms/people', exact: true },
        { id: 'org-chart', label: 'Org chart', icon: 'Network', to: '/hrms/people/org-chart' },
      ],
    },
    {
      id: 'lifecycle',
      label: 'Lifecycle',
      divider: true,
      rows: [
        // The prototype's ADMIN rows: the same key as their routes, so a row never leads to a lock screen.
        // A reporting manager without the pre-joining key reaches their own joiners from the Dashboard.
        // Pre-joining / Onboarding hidden for now (FE only).
        // {
        //   id: 'pre-joining',
        //   label: 'Pre-joining',
        //   icon: 'Contact',
        //   to: '/hrms/people/pre-joining',
        //   visibleWhen: { permission: P.PREJOINING_VIEW },
        // },
        // {
        //   id: 'onboarding',
        //   label: 'Onboarding',
        //   icon: 'UserPlus',
        //   to: '/hrms/people/onboarding',
        //   // Exact, or it would stay lit on its Onboarding tasks page.
        //   exact: true,
        //   visibleWhen: { permission: P.PEOPLE_MANAGE },
        // },
        // // Owners include managers and the IT owner, so anyone who opens People sees it; the list is their own.
        // {
        //   id: 'onboarding-tasks',
        //   label: 'Onboarding tasks',
        //   icon: 'ListTodo',
        //   to: '/hrms/people/onboarding/tasks',
        // },
        {
          id: 'probation',
          label: 'Probation',
          icon: 'Hourglass',
          to: '/hrms/people/probation',
          visibleWhen: { permission: P.PEOPLE_MANAGE },
        },
      ],
    },
  ],
};

// Panel ids are global across apps, so 'hrms-attendance'. Every row carries its route's key, so a row
// never leads to a lock screen; the rail opens on own or team access, so HR without view.own gets in.
// Its setup (Policies, Offices, Shifts) lives in Settings.
const attendancePanel: PanelConfig = {
  id: 'hrms-attendance',
  title: 'Attendance',
  sections: [
    {
      id: 'attendance',
      rows: [
        // Exact, or it would stay lit on every page under /hrms/attendance.
        {
          id: 'my-attendance',
          label: 'My attendance',
          icon: 'CalendarCheck',
          to: '/hrms/attendance',
          exact: true,
          visibleWhen: { permission: P.ATTENDANCE_VIEW_OWN },
        },
        {
          id: 'my-requests',
          label: 'My requests',
          icon: 'FileText',
          to: '/hrms/attendance/requests',
          visibleWhen: { permission: P.ATTENDANCE_VIEW_OWN },
        },
        {
          id: 'team-requests',
          label: 'Team requests',
          icon: 'Inbox',
          to: '/hrms/attendance/team-requests',
          visibleWhen: { permission: [P.ATTENDANCE_VIEW_TEAM, P.ATTENDANCE_VIEW_ALL], permissionAny: true },
        },
        {
          id: 'team',
          label: 'Team',
          icon: 'Users',
          to: '/hrms/attendance/team',
          visibleWhen: { permission: [P.ATTENDANCE_VIEW_TEAM, P.ATTENDANCE_VIEW_ALL], permissionAny: true },
        },
        {
          id: 'roster',
          label: 'Roster',
          icon: 'CalendarRange',
          to: '/hrms/attendance/roster',
          visibleWhen: { permission: P.ATTENDANCE_ROSTER_MANAGE },
        },
        {
          id: 'verification',
          label: 'Verification',
          icon: 'ClipboardCheck',
          to: '/hrms/attendance/verification',
          visibleWhen: { permission: P.ATTENDANCE_VERIFY },
        },
      ],
    },
  ],
};

// Panel ids are global across apps, so 'hrms-leave'. Mirrors Attendance / Overtime: My leave and
// Leave history ride the rail item's key; the team rows and HR's Balances / Register / Year-end carry their route's key,
// and the pilot's surfaces their route's feature flag too. Leave types and plans live in Settings.
const LEAVE_TEAM = { permission: [P.LEAVE_VIEW_TEAM, P.LEAVE_VIEW_ALL], permissionAny: true, flag: FLAG.LEAVE_TEAM_PAGES };

// const LEAVE_HR = { permission: P.LEAVE_VIEW_ALL, flag: FLAG.LEAVE_HR_TOOLS };

const leavePanel: PanelConfig = {
  id: 'hrms-leave',
  title: 'Leave',
  sections: [
    {
      id: 'leave',
      rows: [
        // Exact, or it would stay lit on every page under /hrms/leave.
        { id: 'my-leave', label: 'My leave', icon: 'CalendarMinus2', to: '/hrms/leave', exact: true },
        { id: 'history', label: 'Leave history', icon: 'Clock', to: '/hrms/leave/requests' },
        {
          id: 'team-requests',
          label: 'Team requests',
          icon: 'Inbox',
          to: '/hrms/leave/team-requests',
          visibleWhen: LEAVE_TEAM,
        },
        { id: 'team', label: 'Team', icon: 'Users', to: '/hrms/leave/team', visibleWhen: LEAVE_TEAM },
        // { id: 'balances', label: 'Balances', icon: 'Wallet', to: '/hrms/leave/balances', visibleWhen: LEAVE_HR },
        // { id: 'register', label: 'Register', icon: 'ClipboardList', to: '/hrms/leave/register', visibleWhen: LEAVE_HR },
        { id: 'year-end', label: 'Year-end', icon: 'RefreshCw', to: '/hrms/leave/year-end', visibleWhen: { permission: P.LEAVE_EDIT, flag: FLAG.LEAVE_YEAR_END } },
      ],
    },
  ],
};

// Mirrors the Attendance panel: My overtime rides the rail item's key; the team rows carry their
// route's key, so a row never leads to a lock screen. Overtime policies live in Settings.
// The overtime pilot flag hides every row but My overtime, which pairs with the (unflaggable) rail item and opens
// a "switched off" page.
const OT_TEAM = { permission: [P.OVERTIME_VIEW_TEAM, P.OVERTIME_VIEW_ALL], permissionAny: true, flag: FLAG.OVERTIME };

const overtimePanel: PanelConfig = {
  id: 'hrms-overtime',
  title: 'Overtime',
  sections: [
    {
      id: 'overtime',
      rows: [
        // Exact, or it would stay lit on every page under /hrms/overtime.
        { id: 'my-overtime', label: 'My overtime', icon: 'Timer', to: '/hrms/overtime', exact: true },
        {
          id: 'team-requests',
          label: 'Team requests',
          icon: 'Inbox',
          to: '/hrms/overtime/team-requests',
          visibleWhen: OT_TEAM,
        },
      ],
    },
  ],
};

const ATT_SETUP = { permission: P.ATTENDANCE_POLICY_MANAGE };
const LEAVE_SETUP = { permission: P.LEAVE_SETUP_MANAGE };
const OT_SETUP = { permission: P.OVERTIME_POLICY_MANAGE, flag: FLAG.OVERTIME };
// const ONBOARDING_SETUP = { permission: P.ONBOARDING_TEMPLATES_MANAGE };

// Module setup comes first: an admin configures each module here before anyone uses it. One row per
// module; its pages are tabs on that module's settings page.
// Only the rows that are built: the prototype's other Settings rows join as their pages land.
const settingsPanel: PanelConfig = {
  id: 'hrms-settings',
  title: 'Settings',
  sections: [
    {
      id: 'modules',
      label: 'Modules',
      visibleWhen: {
        permission: [P.ATTENDANCE_POLICY_MANAGE, P.LEAVE_SETUP_MANAGE, P.OVERTIME_POLICY_MANAGE /* , P.ONBOARDING_TEMPLATES_MANAGE */],
        permissionAny: true,
      },
      rows: [
        { id: 'attendance-setup', label: 'Attendance', icon: 'UserCheck', to: '/hrms/settings/attendance', visibleWhen: ATT_SETUP },
        { id: 'leave-setup', label: 'Leave', icon: 'CalendarMinus2', to: '/hrms/settings/leave', visibleWhen: LEAVE_SETUP },
        { id: 'overtime-setup', label: 'Overtime', icon: 'Timer', to: '/hrms/settings/overtime', visibleWhen: OT_SETUP },
        // { id: 'onboarding-setup', label: 'Onboarding', icon: 'ClipboardCheck', to: '/hrms/settings/onboarding', visibleWhen: ONBOARDING_SETUP },
      ],
    },
    {
      id: 'masters',
      label: 'Masters',
      divider: true,
      rows: [
        {
          id: 'attendance-statuses',
          label: 'Attendance Statuses',
          icon: 'Tag',
          to: '/hrms/settings/attendance-statuses',
          visibleWhen: { permission: P.SETTINGS_MANAGE },
        },
        {
          id: 'leave-categories',
          label: 'Leave Categories',
          icon: 'Layers',
          to: '/hrms/settings/leave-categories',
          visibleWhen: { permission: P.SETTINGS_MANAGE },
        },
        {
          id: 'leave-reasons',
          label: 'Leave Reasons',
          icon: 'MessageSquare',
          to: '/hrms/settings/leave-reasons',
          visibleWhen: { permission: P.SETTINGS_MANAGE },
        },
        {
          id: 'request-types',
          label: 'Request Types',
          icon: 'Layers',
          to: '/hrms/settings/request-types',
          visibleWhen: { permission: P.SETTINGS_MANAGE },
        },
        {
          id: 'request-statuses',
          label: 'Request Statuses',
          icon: 'CircleDot',
          to: '/hrms/settings/request-statuses',
          visibleWhen: { permission: P.SETTINGS_MANAGE },
        },
        {
          id: 'day-types',
          label: 'Day Types',
          icon: 'CalendarDays',
          to: '/hrms/settings/day-types',
          visibleWhen: { permission: P.SETTINGS_MANAGE },
        },
      ],
    },
    {
      id: 'configuration',
      label: 'Configuration',
      divider: true,
      rows: [
        {
          id: 'approval-workflows',
          label: 'Approval workflows',
          icon: 'Workflow',
          to: '/hrms/settings/approval-workflows',
          visibleWhen: { permission: P.SETTINGS_MANAGE },
        },
        // {
        //   id: 'employee-groups',
        //   label: 'Employee groups',
        //   icon: 'UsersRound',
        //   to: '/hrms/settings/employee-groups',
        //   visibleWhen: { permission: P.SETTINGS_MANAGE },
        // },
        {
          id: 'form-builder',
          label: 'Form & field builder',
          icon: 'LayoutList',
          to: '/hrms/settings/forms',
          visibleWhen: { permission: P.SETTINGS_MANAGE },
        },
      ],
    },
    {
      id: 'access',
      label: 'Access',
      divider: true,
      rows: [
        {
          id: 'roles-permissions',
          label: 'Roles & Permissions',
          icon: 'Shield',
          to: '/hrms/settings/roles-permissions',
          visibleWhen: { permission: P.RBAC_VIEW },
        },
      ],
    },
  ],
};

export const hrmsSidebar: SidebarConfig = {
  version: SIDEBAR_SCHEMA_VERSION,
  panels: [peoplePanel, attendancePanel, leavePanel, overtimePanel, settingsPanel],
};
